"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "@/lib/query/keys";
import type {
  WorkspacePreferenceDto,
  WorkspacePreferenceScope,
  WorkspacePreferenceState,
  WorkspacePreferenceUpdateRequest,
} from "@/types/workspace-preferences";
import {
  cacheWorkspacePreference,
  readCachedWorkspacePreference,
} from "./cache";

type PersistentWorkspaceStateStatus =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "error"; error: unknown };

type PersistentStateSetter<T> = (value: T | ((previous: T) => T)) => void;

const WRITE_DEBOUNCE_MS = 500;

function createEmptyPreference(scope: WorkspacePreferenceScope): WorkspacePreferenceDto<WorkspacePreferenceState> {
  return {
    scope,
    revision: 0,
    updatedAt: new Date().toISOString(),
    state: {},
  };
}

function useWorkspacePreferenceQuery(scope: WorkspacePreferenceScope, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.workspacePreference(scope),
    queryFn: () => readCachedWorkspacePreference(scope) ?? createEmptyPreference(scope),
    initialData: () => readCachedWorkspacePreference(scope),
    enabled,
    staleTime: Infinity,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    meta: { scope },
  });
}

export function useWorkspacePreferenceBootstrap() {
  // Workspace preferences are stored locally only; no API sync needed.
}

export function usePersistentWorkspaceState<TValue = unknown>(
  scope: WorkspacePreferenceScope,
  key: string,
  defaultValue: TValue,
  options?: { enabled?: boolean },
): [TValue, PersistentStateSetter<TValue>, PersistentWorkspaceStateStatus] {
  const enabled = options?.enabled ?? true;
  const queryClient = useQueryClient();
  const preferenceQuery = useWorkspacePreferenceQuery(scope, enabled);

  const mutation = useMutation({
    mutationFn: async (input: WorkspacePreferenceUpdateRequest<WorkspacePreferenceState>) => {
      const latest = queryClient.getQueryData<WorkspacePreferenceDto>(queryKeys.workspacePreference(scope))
        ?? createEmptyPreference(scope);
      const result: WorkspacePreferenceDto = {
        ...latest,
        state: input.state,
        revision: (latest.revision ?? 0) + 1,
        updatedAt: new Date().toISOString(),
      };
      cacheWorkspacePreference(result);
      return result;
    },
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.workspacePreference(scope), result);
    },
  });

  const pendingStateRef = useRef<WorkspacePreferenceState | null>(null);
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const enabledRef = useRef(enabled);

  const clearFlushTimer = useCallback(() => {
    if (flushTimeoutRef.current) {
      clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
    }
  }, []);

  const scheduleFlush = useCallback(() => {
    if (!enabled) {
      pendingStateRef.current = null;
      clearFlushTimer();
      return;
    }

    clearFlushTimer();
    flushTimeoutRef.current = setTimeout(() => {
      flushTimeoutRef.current = null;
      if (!enabledRef.current) {
        pendingStateRef.current = null;
        return;
      }

      const stateToPersist = pendingStateRef.current;
      if (!stateToPersist) {
        return;
      }

      pendingStateRef.current = null;
      const latest = queryClient.getQueryData<WorkspacePreferenceDto>(queryKeys.workspacePreference(scope));

      mutation.mutateAsync({
        state: stateToPersist,
        revision: latest?.revision ?? null,
      }).catch(() => {
        // errors are handled via mutation callbacks; suppress unhandled rejection warnings
      });
    }, WRITE_DEBOUNCE_MS);
  }, [enabled, mutation, queryClient, scope, clearFlushTimer]);

  useEffect(() => {
    enabledRef.current = enabled;
    if (!enabled) {
      pendingStateRef.current = null;
      clearFlushTimer();
    }
  }, [enabled, clearFlushTimer]);

  useEffect(() => clearFlushTimer, [clearFlushTimer]);

  const status: PersistentWorkspaceStateStatus = useMemo(() => {
    if (!enabled) {
      return { status: "loading" };
    }

    if (preferenceQuery.status === "error") {
      return { status: "error", error: preferenceQuery.error };
    }

    if (preferenceQuery.data) {
      return { status: "ready" };
    }

    return { status: "loading" };
  }, [enabled, preferenceQuery.data, preferenceQuery.error, preferenceQuery.status]);

  const currentState = useMemo<WorkspacePreferenceState>(() => {
    if (!preferenceQuery.data?.state) {
      return {};
    }
    return preferenceQuery.data.state as WorkspacePreferenceState;
  }, [preferenceQuery.data]);

  const currentValue = useMemo<TValue>(() => {
    if (!enabled) {
      return defaultValue;
    }

    const storedValue = currentState[key];
    return (storedValue === undefined ? defaultValue : (storedValue as TValue));
  }, [currentState, defaultValue, enabled, key]);

  const setValue: PersistentStateSetter<TValue> = useCallback(
    (next) => {
      if (!enabled) {
        return;
      }

      const preferenceKey = queryKeys.workspacePreference(scope);
      const latest = queryClient.getQueryData<WorkspacePreferenceDto>(preferenceKey) ?? createEmptyPreference(scope);
      const baseState: WorkspacePreferenceState = { ...(latest.state ?? currentState) };
      const previousValue = (baseState[key] === undefined ? defaultValue : (baseState[key] as TValue));
      const resolvedValue = typeof next === "function" ? (next as (prev: TValue) => TValue)(previousValue) : next;

      if (Object.is(previousValue, resolvedValue)) {
        return;
      }

      const nextState = { ...baseState };
      if (resolvedValue === undefined) {
        delete nextState[key];
      } else {
        nextState[key] = resolvedValue as unknown;
      }

      const nextPreference: WorkspacePreferenceDto = {
        ...latest,
        state: nextState,
        updatedAt: new Date().toISOString(),
      };

      cacheWorkspacePreference(nextPreference);
      queryClient.setQueryData(preferenceKey, nextPreference);
      pendingStateRef.current = nextState;
      scheduleFlush();
    },
    [currentState, defaultValue, enabled, key, queryClient, scheduleFlush, scope],
  );

  return [currentValue, setValue, status];
}
